import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook } from 'lucide-react';
import { counties } from '../data/countyData';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-navy text-white">
      <div className="container-custom pt-10 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Company Info */}
          <div>
            <div className="mb-4 flex flex-col items-start">
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img
                  src="/logo.png"
                  alt="Elevation Electrical, LLC Logo"
                  width={64}
                  height={64}
                  loading="lazy"
                  decoding="async"
                  className="h-16 w-auto mb-3 bg-white rounded-full p-1"
                />
              </picture>
              <h3 className="text-lg font-bold text-white">Elevation Electrical, LLC</h3>
            </div>
            <p className="mb-2 text-gray-300 text-sm">Trusted electrical contractor serving Lake Helen, Florida, and surrounding areas.</p>
            <p className="text-sm text-gray-300">EC13011028</p>
            <div className="mt-3 flex space-x-3">
              <a
                href="https://www.facebook.com/profile.php?id=100089725775324"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-orange transition-colors"
              >
                <span className="sr-only">Facebook</span>
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-base font-semibold text-white mb-3">Contact Us</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-start">
                <MapPin className="mr-2 h-4 w-4 text-orange flex-shrink-0 mt-1" />
                <span>Lake Helen, FL &mdash; serving Volusia, Seminole, Orange &amp; surrounding counties</span>
              </li>
              <li className="flex items-center">
                <Phone className="mr-2 h-4 w-4 text-orange flex-shrink-0" />
                <a href="tel:4079707211" className="hover:text-orange transition-colors">(407) 970-7211</a>
              </li>
              <li className="flex items-center">
                <Mail className="mr-2 h-4 w-4 text-orange flex-shrink-0" />
                <a href="mailto:sales@elevationelectricalfl.com" className="hover:text-orange transition-colors">sales@elevationelectricalfl.com</a>
              </li>
            </ul>

            {/* Top Cities */}
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Top Service Cities</h4>
            <ul className="space-y-1 text-gray-300 text-sm grid grid-cols-2 gap-x-2 gap-y-1">
              <li><Link to="/locations/lake-helen" className="hover:text-orange transition-colors">Lake Helen</Link></li>
              <li><Link to="/locations/deland" className="hover:text-orange transition-colors">DeLand</Link></li>
              <li><Link to="/locations/deltona" className="hover:text-orange transition-colors">Deltona</Link></li>
              <li><Link to="/locations/daytona-beach" className="hover:text-orange transition-colors">Daytona Beach</Link></li>
              <li><Link to="/locations/orlando" className="hover:text-orange transition-colors">Orlando</Link></li>
              <li><Link to="/locations/sanford" className="hover:text-orange transition-colors">Sanford</Link></li>
              <li><Link to="/locations/winter-park" className="hover:text-orange transition-colors">Winter Park</Link></li>
              <li><Link to="/locations/ormond-beach" className="hover:text-orange transition-colors">Ormond Beach</Link></li>
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-base font-semibold text-white mb-3">Our Services</h4>
            <ul className="space-y-1 text-gray-300 text-sm">
              <li><Link to="/services/residential" className="hover:text-orange transition-colors">Residential Services</Link></li>
              <li><Link to="/services/commercial" className="hover:text-orange transition-colors">Commercial Services</Link></li>
              <li><Link to="/services/new-construction" className="hover:text-orange transition-colors">New Construction</Link></li>
              <li><Link to="/services/remodels" className="hover:text-orange transition-colors">Remodels</Link></li>
              <li><Link to="/services/panel-upgrades" className="hover:text-orange transition-colors">Panel Upgrades</Link></li>
              <li><Link to="/services/generators" className="hover:text-orange transition-colors">Generator Installation</Link></li>
              <li><Link to="/services/ev-charging" className="hover:text-orange transition-colors">EV Charger Installation</Link></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-base font-semibold text-white mb-3">Quick Links</h4>
            <ul className="space-y-1 text-gray-300 text-sm">
              <li><Link to="/about" className="hover:text-orange transition-colors">About Us</Link></li>
              <li><Link to="/locations" className="hover:text-orange transition-colors">Service Areas</Link></li>
              <li><Link to="/blog" className="hover:text-orange transition-colors">Blog &amp; Guides</Link></li>
              <li><Link to="/faq" className="hover:text-orange transition-colors">FAQ</Link></li>
              <li><Link to="/affiliations" className="hover:text-orange transition-colors">Affiliations</Link></li>
              <li><Link to="/now-hiring" className="hover:text-orange transition-colors">Now Hiring</Link></li>
              <li><Link to="/contact" className="hover:text-orange transition-colors">Contact</Link></li>
            </ul>

            {/* Counties We Serve */}
            <h4 className="text-base font-semibold text-white mt-4 mb-2">Counties We Serve</h4>
            <ul className="space-y-1 text-gray-300 text-sm">
              {counties.map((county) => (
                <li key={county.slug}>
                  <Link to={`/locations/counties/${county.slug}`} className="hover:text-orange transition-colors">
                    {county.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Bottom Section - Elevation Family */}
        <div className="mt-8 pb-4 border-t border-white/20 pt-4">
          <div className="text-center">
            <h4 className="text-base font-semibold text-white mb-2">Part of the Elevation Family</h4>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 text-xs">
              <a href="https://elevationwindowsfl.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-orange transition-colors">
                Elevation Windows and Doors
              </a>
              <span className="hidden sm:block text-white/30">|</span>
              <a href="https://elevationbuildersgroup.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-orange transition-colors">
                Elevation Builders Group, LLC
              </a>
            </div>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="mt-6 text-center text-xs text-gray-400">
          <p>&copy; {currentYear} Elevation Electrical, LLC. All rights reserved.</p>
          <p className="mt-2">
            {/* text-orange (#ff7a00) on bg-navy (#0f2c52) = 5.44:1, passes
                WCAG AA. Underlined for extra distinguishability. */}
            Powered by <a href="https://1websitenow.com" target="_blank" rel="noopener noreferrer" className="text-orange underline underline-offset-2 hover:text-orange-light">1WebsiteNow&trade;</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
